import React from 'react';
import ReactDOM from 'react-dom/client';
import './i18n'; // Import i18n configuration
import App from './App';
import MinimalApp from './MinimalApp';
import './index.css';

import { ThemeProvider, createTheme } from '@mui/material/styles';

// Error Boundary Component for debugging
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('🚨 React Error Boundary caught an error:');
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Stack:', error.stack);
    this.setState({ error, errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', fontFamily: 'monospace' }}>
          <h1>🚨 React Error Detected</h1>
          <h2>Error: {this.state.error && this.state.error.toString()}</h2>
          <details style={{ whiteSpace: 'pre-wrap' }}>
            <summary>Error Stack</summary>
            {this.state.error && this.state.error.stack}
          </details>
          <details style={{ whiteSpace: 'pre-wrap' }}>
            <summary>Component Stack</summary>
            {this.state.errorInfo?.componentStack || 'No component stack available'}
          </details>
        </div>
      );
    }
    return this.props.children;
  }
}

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#6366f1', light: '#818cf8', dark: '#4f46e5', contrastText: '#ffffff' },
    secondary: { main: '#818cf8', light: '#a5b4fc', dark: '#6366f1', contrastText: '#ffffff' },
    success: { main: '#10b981', light: '#34d399', dark: '#059669' },
    warning: { main: '#f59e0b', light: '#fbbf24', dark: '#d97706' },
    error: { main: '#ef4444', light: '#f87171', dark: '#dc2626' },
    info: { main: '#0ea5e9', light: '#38bdf8', dark: '#0284c7' },
    background: { default: '#0d1117', paper: '#161b22' },
    text: { primary: '#e6edf3', secondary: '#8b949e', disabled: '#6e7681' },
    divider: 'rgba(99,102,241,0.2)',
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    button: { fontWeight: 600, textTransform: 'none' },
  },
  components: {
    MuiPaper: {
      styleOverrides: { root: { backgroundImage: 'none' } },
    },
    MuiDialog: {
      styleOverrides: { paper: { backgroundImage: 'none' } },
    },
  },
});


const root = ReactDOM.createRoot(document.getElementById('root'));

try {
  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <ThemeProvider theme={theme}>
          <App />
        </ThemeProvider>
      </ErrorBoundary>
    </React.StrictMode>
  );
} catch (error) {
  console.error('🚨 INDEX.JSX: Render failed:', error);
  console.error('Stack:', error.stack);
}

// Offline app-shell via service worker was removed — it was unreliable on iOS
// Safari and caused load failures. We now run as a plain online web app (data is
// still cached locally in IndexedDB for instant loads while online). This cleans
// up any service worker / caches left on devices from earlier attempts.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.getRegistrations()
      .then(regs => regs.forEach(reg => reg.unregister()))
      .catch(() => {});
    if (window.caches && caches.keys) {
      caches.keys().then(keys => keys.forEach(k => caches.delete(k))).catch(() => {});
    }
  });
}

